import React, { useState, useEffect } from "react";
import "../../_css/ValidationFindingsModal.css";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { notifyError, notifySuccess } from "../../utils/helper";
import { 
  getValidationFindings, 
  proceedWithEvaluation, 
  cancelEvaluation 
} from "../../requests";

const ValidationFindingsModal = ({ answersheetId, fn, onSuccess }) => {
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchFindings = async () => {
    dispatch(startLoader());
    try {
      const res = await getValidationFindings(answersheetId);
      setData(res.data);
    } catch (error) {
      notifyError(error?.response?.data?.error || "Failed to load validation findings");
      fn();
    } finally {
      dispatch(stopLoader());
      setLoading(false);
    }
  };

  const handleProceed = async () => {
    dispatch(startLoader());
    try {
      const res = await proceedWithEvaluation(answersheetId);
      if (res.status < 400) {
        notifySuccess("Evaluation started. You will receive an email when complete.");
        fn();
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      notifyError(error?.response?.data?.error || "Failed to start evaluation");
    } finally {
      dispatch(stopLoader());
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel this evaluation? Credits will be refunded.")) {
      return;
    }

    dispatch(startLoader());
    try {
      const res = await cancelEvaluation(answersheetId);
      if (res.status < 400) {
        notifySuccess("Evaluation cancelled. Credits have been refunded.");
        fn();
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      notifyError(error?.response?.data?.error || "Failed to cancel evaluation");
    } finally {
      dispatch(stopLoader());
    }
  };

  useEffect(() => {
    if (answersheetId) {
      fetchFindings();
    }
  }, [answersheetId]);

  if (loading) {
    return null;
  }

  return (
    <div className="validation-findings-overlay" onClick={fn}>
      <div className="validation-findings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="validation-findings-header">
          <h2>⚠️ Action Required - Review Validation Findings</h2>
          <button className="close-btn" onClick={fn}>✕</button>
        </div>

        <div className="validation-findings-content">
          {/* Paper Info */}
          <div className="paper-info">
            {/* <div className="info-item">
              <strong>Student:</strong> {data?.student_name}
            </div> */}
            <div className="info-item">
              <strong>Subject:</strong> {data?.subject}
            </div>
            <div className="info-item">
              <strong>Grade:</strong> {data?.grade}
            </div>
            <div className="info-item">
              <strong>Exam:</strong> {data?.exam_name}
            </div>
          </div>

          {/* Findings */}
          <div className="findings-section">
            <h3>Validation Findings ({data?.findings?.length || 0})</h3>
            <p className="findings-description">
              The following issues were found during validation. Please review them carefully before deciding to proceed with evaluation.
            </p>

            {data?.findings && data.findings.length > 0 ? (
              <div className="findings-list">
                {data.findings.map((finding, idx) => (
                  <div 
                    key={idx} 
                    className={`finding-card finding-${finding.severity || 'info'}`}
                  >
                    <div className="finding-header">
                      <span className={`severity-badge severity-${finding.severity || 'info'}`}>
                        {(finding.severity || 'info').toUpperCase()}
                      </span>
                      <span className="finding-title">{finding.title}</span>
                      {finding.marks_at_risk && (
                        <span className="marks-at-risk">
                          {finding.marks_at_risk} marks at risk
                        </span>
                      )}
                    </div>

                    <div className="finding-description">
                      {finding.description}
                    </div>

                    {finding.recommendation && (
                      <div className="finding-recommendation">
                        <strong>→ Recommendation:</strong> {finding.recommendation}
                      </div>
                    )}

                    {finding.flagged_questions && finding.flagged_questions.length > 0 && (
                      <div className="flagged-questions">
                        <strong>Affected Questions:</strong>
                        <ul>
                          {finding.flagged_questions.map((q, i) => (
                            <li key={i}>{q}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-findings">No findings to display.</p>
            )}
          </div>
        </div>

        <div className="validation-findings-footer">
          <div className="footer-message">
            <p>
              <strong>Note:</strong> These findings indicate potential issues with the question paper or memo. 
              You can choose to proceed with evaluation anyway, or cancel and fix the issues first.
            </p>
          </div>
          <div className="action-buttons">
            <button 
              className="btn-cancel" 
              onClick={handleCancel}
            >
              Abort Evaluation
            </button>
            <button 
              className="btn-proceed" 
              onClick={handleProceed}
            >
              Proceed with Evaluation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValidationFindingsModal;
